import { useEffect, useState } from 'react';
import { employeVersDto } from '../../api/mappers';
import { employesApi, invitationsApi } from '../../api/resources';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { ROLES, ficheEnregistree, nomComplet } from '../../lib/acces';
import { ACCESS_ROLE } from '../../lib/access';
import { tr, trf } from '../../lib/i18n';
import { separeLEffectif } from '../../lib/personnel';
import { C } from '../../theme/palette';

/** Une adresse qui a la forme d'une adresse ; le serveur d'envoi tranche le reste. */
const ADRESSE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const encadre = {
  background: C.white,
  border: '1px solid ' + C.border,
  borderRadius: 12,
  padding: 11,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const choixDuRole = {
  flexShrink: 0,
  maxWidth: 130,
  border: '1px solid ' + C.border,
  borderRadius: 8,
  padding: '6px 4px',
  fontSize: 10,
  background: C.white,
};

/** Rôle à proposer : celui que le serveur suggère, s'il existe, sinon ouvrier. */
const roleConnu = (role) => (ROLES.some((r) => r.cle === role) ? role : 'OUVRIER');

/**
 * « ✉️ Inviter toute l'équipe » : chaque personne en poste qui n'a pas encore
 * d'accès reçoit son invitation, d'un geste.
 *
 * <p>« Pas encore d'accès » : ni compte ouvert (le compte de l'ancien code PIN
 * ne compte pas quand ce code n'ouvre plus de session), ni invitation encore
 * valable — celle-là se renvoie depuis la fiche, pas sans le vouloir. Le
 * serveur le dit pour toute l'équipe en une lecture, et ne rend au technicien
 * et au directeur des travaux que les ouvriers.
 *
 * <p>L'adresse est celle de la fiche, que le contrat reprend. Celle qu'on
 * ajoute ici s'enregistre sur la fiche avant l'envoi. Chaque invitation part
 * ensuite par le même chemin que « ✉️ Inviter » sur la fiche : même lien,
 * même e-mail, mêmes règles.
 *
 * @param {object} props propriétés du composant
 * @param {object[]} props.employes fiches de l'équipe
 * @param {() => void} props.onFerme fermeture de l'écran
 * @param {() => Promise<void>} [props.rechargeEmployes] relecture des fiches,
 *        après l'ajout d'adresses
 * @param {(employe: object) => boolean} [props.estLaMienne] vrai pour la fiche du
 *        patron connecté, qui n'a rien à recevoir
 * @returns {import('react').ReactElement} l'écran
 */
export function InviterEquipe({ employes, onFerme, rechargeEmployes, estLaMienne }) {
  const patron = ACCESS_ROLE === 'patron';
  const [acces, setAcces] = useState(null);
  const [erreurLecture, setErreurLecture] = useState('');
  const [decoches, setDecoches] = useState({});
  const [adresses, setAdresses] = useState({});
  const [roles, setRoles] = useState({});
  const [envoi, setEnvoi] = useState(false);
  const [resultats, setResultats] = useState(null);
  const [copie, setCopie] = useState(null);

  useEffect(() => {
    let actif = true;
    Promise.resolve()
      .then(() => invitationsApi.equipe())
      .then((l) => {
        if (actif) setAcces(Array.isArray(l) ? l : []);
      })
      .catch((e) => {
        if (!actif) return;
        setErreurLecture(e?.message || 'La demande n’est pas partie.');
        setAcces([]);
      });
    return () => {
      actif = false;
    };
  }, []);

  const parFiche = new Map((acces || []).map((a) => [a.employeId, a]));
  const miennes = (e) => (estLaMienne ? estLaMienne(e) : false);
  const fiches = separeLEffectif(employes || []).effectif.filter(
    (e) => ficheEnregistree(e) && parFiche.has(e.id),
  );
  const aInviter = fiches
    .filter((e) => !miennes(e) && parFiche.get(e.id).acces === 'AUCUN')
    .sort((a, b) => [a.nom, a.prenom].join(' ').localeCompare([b.nom, b.prenom].join(' '), 'fr'));
  const avecAdresse = aInviter.filter((e) => String(e.email || '').trim());
  const sansAdresse = aInviter.filter((e) => !String(e.email || '').trim());
  const dejaUnAcces = fiches.filter(
    (e) => miennes(e) || parFiche.get(e.id).acces === 'COMPTE',
  ).length;
  const enCours = fiches.filter(
    (e) => !miennes(e) && parFiche.get(e.id).acces === 'INVITATION',
  ).length;

  const roleDe = (e) => (patron ? roles[e.id] || roleConnu(parFiche.get(e.id)?.role) : 'OUVRIER');
  const saisie = (e) => String(adresses[e.id] || '').trim();
  const cibles = [
    ...avecAdresse
      .filter((e) => !decoches[e.id])
      .map((e) => ({ emp: e, email: String(e.email).trim(), nouvelle: false })),
    ...sansAdresse
      .filter((e) => ADRESSE.test(saisie(e)))
      .map((e) => ({ emp: e, email: saisie(e), nouvelle: true })),
  ];
  const n = cibles.length;

  const envoie = async () => {
    if (n === 0 || envoi) return;
    setEnvoi(true);
    const faits = [];
    // Une à une, comme « ✉️ Inviter » sur chaque fiche : un refus n'arrête
    // pas les suivantes.
    for (const c of cibles) {
      const r = { id: c.emp.id, nom: nomComplet(c.emp) || c.emp.nom, email: c.email };
      try {
        if (c.nouvelle) {
          await employesApi.metAJour(c.emp.id, employeVersDto({ ...c.emp, email: c.email }));
        }
        const inv = await invitationsApi.invite(c.emp.id, roleDe(c.emp));
        r.ok = true;
        r.envoyee = !!inv?.courrielEnvoyeLe;
        r.courrielErreur = inv?.courrielErreur || null;
        r.lien = inv?.lien || '';
      } catch (e) {
        r.ok = false;
        r.erreur = e?.message || 'L’invitation n’a pas pu être envoyée.';
      }
      faits.push(r);
      setResultats([...faits]);
    }
    setEnvoi(false);
    if (cibles.some((c) => c.nouvelle) && rechargeEmployes) {
      Promise.resolve()
        .then(() => rechargeEmployes())
        .catch(() => {});
    }
  };

  const copieLeLien = async (r) => {
    try {
      await navigator.clipboard.writeText(r.lien);
      setCopie(r.id);
    } catch {
      setCopie(null);
    }
  };

  const nomEtFonction = (e) => (
    <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <b style={{ fontSize: 11 }}>
        {nomComplet(e) || e.nom}
        {e.role ? ' · ' + e.role : ''}
      </b>
      {String(e.email || '').trim() && (
        <span style={{ fontSize: 9.5, color: C.grey, overflowWrap: 'anywhere' }}>{e.email}</span>
      )}
    </span>
  );

  const selectRole = (e) =>
    patron ? (
      <select
        aria-label={trf('Accès de {0}', nomComplet(e) || e.nom || '')}
        value={roleDe(e)}
        disabled={envoi}
        onChange={(ev) => setRoles((p) => ({ ...p, [e.id]: ev.target.value }))}
        style={choixDuRole}
      >
        {ROLES.map((r) => (
          <option key={r.cle} value={r.cle}>
            {r.nom}
          </option>
        ))}
      </select>
    ) : null;

  const entete = (
    <div
      style={{
        background: C.navy,
        color: '#fff',
        padding: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <b style={{ fontSize: 13 }}>✉️ Inviter toute l'équipe</b>
      <span style={{ fontSize: 9.5, opacity: 0.85 }}>
        Les adresses viennent des fiches et des contrats
      </span>
    </div>
  );

  if (resultats) {
    const reussies = resultats.filter((r) => r.ok).length;
    return (
      <Card style={{ marginBottom: 12, overflow: 'hidden', border: '2px solid ' + C.green }}>
        {entete}
        <div
          data-testid="resultats-invitations"
          style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          {resultats.map((r) => (
            <div key={r.id} style={{ ...encadre, gap: 3 }} data-testid={'resultat-' + r.id}>
              <b style={{ fontSize: 11 }}>{r.nom}</b>
              {r.ok && r.envoyee && (
                <span style={{ fontSize: 9.5, fontWeight: 700, color: C.green }}>
                  {trf('✓ Envoyée à {0}', r.email)}
                </span>
              )}
              {r.ok && !r.envoyee && (
                <>
                  <span style={{ fontSize: 9.5, fontWeight: 700, color: C.amber, lineHeight: 1.5 }}>
                    ⚠️{' '}
                    {r.courrielErreur === 'NON_RACCORDE'
                      ? tr(
                          'L’envoi par e-mail n’est pas raccordé : partage le lien par WhatsApp, SMS ou en le copiant.',
                        )
                      : trf(
                          'L’e-mail à {0} n’est pas parti : vérifie l’adresse, ou partage le lien par WhatsApp, SMS ou en le copiant.',
                          r.email,
                        )}
                  </span>
                  {r.lien && (
                    <div>
                      <button
                        type="button"
                        onClick={() => copieLeLien(r)}
                        style={{
                          border: '1px solid ' + C.navy,
                          background: C.white,
                          color: C.navy,
                          borderRadius: 8,
                          padding: '6px 10px',
                          fontSize: 9.5,
                          fontWeight: 800,
                          cursor: 'pointer',
                        }}
                      >
                        {copie === r.id ? '✓ Copié' : '📋 Copier le lien'}
                      </button>
                    </div>
                  )}
                </>
              )}
              {!r.ok && (
                <span role="alert" style={{ fontSize: 9.5, color: C.red, lineHeight: 1.5 }}>
                  <span>✕</span> <span>{r.erreur}</span>
                </span>
              )}
            </div>
          ))}
          {envoi ? (
            <div role="status" style={{ fontSize: 10, color: C.muted }}>
              Envoi…
            </div>
          ) : (
            <>
              <div style={{ fontSize: 10.5, fontWeight: 800, color: C.ink }}>
                {trf('{0} sur {1} invitations envoyées.', reussies, resultats.length)}
              </div>
              <Btn label="Fermer" ghost color={C.navy} full onClick={onFerme} />
            </>
          )}
        </div>
      </Card>
    );
  }

  return (
    <Card style={{ marginBottom: 12, overflow: 'hidden', border: '2px solid ' + C.navy }}>
      {entete}
      <div
        data-testid="inviter-equipe"
        style={{
          padding: 12,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          background: C.navyXL,
        }}
      >
        {acces === null ? (
          <div role="status" style={{ fontSize: 10, color: C.muted }}>
            Chargement…
          </div>
        ) : (
          <>
            {erreurLecture && (
              <div role="alert" style={{ fontSize: 10, color: C.red }}>
                {erreurLecture}
              </div>
            )}
            {!erreurLecture && aInviter.length === 0 && (
              <div style={{ ...encadre, fontSize: 10.5 }}>
                Tout le monde a déjà un accès ou une invitation en cours.
              </div>
            )}
            {avecAdresse.length > 0 && (
              <div style={encadre} data-testid="groupe-avec-adresse">
                <b style={{ fontSize: 11.5 }}>
                  {trf('Pas encore d’accès ({0})', avecAdresse.length)}
                </b>
                {avecAdresse.map((e, i) => (
                  <div
                    key={e.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '7px 0',
                      borderBottom: i < avecAdresse.length - 1 ? '1px solid ' + C.border : 'none',
                    }}
                  >
                    <label
                      style={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={!decoches[e.id]}
                        disabled={envoi}
                        onChange={(ev) =>
                          setDecoches((p) => ({ ...p, [e.id]: !ev.target.checked }))
                        }
                        style={{ width: 22, height: 22, flexShrink: 0 }}
                      />
                      {nomEtFonction(e)}
                    </label>
                    {selectRole(e)}
                  </div>
                ))}
              </div>
            )}
            {sansAdresse.length > 0 && (
              <div
                data-testid="groupe-sans-adresse"
                style={{ ...encadre, background: C.orangeL, border: '1px solid #F3C98B', gap: 6 }}
              >
                <b style={{ fontSize: 11.5, color: '#7C2D12' }}>
                  {trf('Sans adresse e-mail sur la fiche ({0})', sansAdresse.length)}
                </b>
                {sansAdresse.map((e) => {
                  const v = saisie(e);
                  return (
                    <div key={e.id} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ flex: 1, minWidth: 0 }}>{nomEtFonction(e)}</span>
                        {selectRole(e)}
                      </div>
                      <input
                        type="email"
                        inputMode="email"
                        autoComplete="off"
                        placeholder="Ajouter son e-mail"
                        aria-label={trf('E-mail de {0}', nomComplet(e) || e.nom || '')}
                        value={adresses[e.id] || ''}
                        disabled={envoi}
                        onChange={(ev) => setAdresses((p) => ({ ...p, [e.id]: ev.target.value }))}
                        style={{
                          border: '1px solid #D1D5DB',
                          borderRadius: 9,
                          padding: 10,
                          fontSize: 14,
                          fontFamily: 'inherit',
                          minWidth: 0,
                        }}
                      />
                      {v && !ADRESSE.test(v) && (
                        <span role="alert" style={{ fontSize: 9.5, color: C.red }}>
                          {tr("Cette adresse e-mail n'est pas valable.")}
                        </span>
                      )}
                    </div>
                  );
                })}
                <span style={{ fontSize: 9.5, color: '#7C2D12', lineHeight: 1.5 }}>
                  L’adresse s’enregistre aussi sur sa fiche. Laissée vide, il n’est pas invité.
                </span>
              </div>
            )}
            <div
              style={{
                background: C.greenL,
                borderRadius: 10,
                padding: '9px 11px',
                fontSize: 9.5,
                color: '#14532D',
                lineHeight: 1.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <span data-testid="deja-un-acces">
                {trf('Déjà un accès : {0} (non listés).', dejaUnAcces)}
              </span>
              {enCours > 0 && (
                <span data-testid="invitations-en-cours">
                  {trf(
                    'Invitation en cours : {0} (non listés), à renvoyer depuis leur fiche.',
                    enCours,
                  )}
                </span>
              )}
              <span>
                {tr(
                  'Chacun reçoit son lien et crée son compte avec son e-mail et son mot de passe.',
                )}
              </span>
              {!patron && (
                <span>
                  {tr('Tu invites les ouvriers ; un accès du bureau, seul le patron l’ouvre.')}
                </span>
              )}
            </div>
          </>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {/* Personne à inviter : pas de bouton « Envoyer les 0 invitations ». */}
          {(n > 0 || envoi) && (
            <Btn
              label={
                envoi
                  ? 'Envoi…'
                  : n === 1
                    ? '✉️ Envoyer l’invitation'
                    : trf('✉️ Envoyer les {0} invitations', n)
              }
              color={C.green}
              full
              disabled={n === 0 || envoi}
              onClick={envoie}
            />
          )}
          <Btn label="Annuler" ghost color={C.grey} full onClick={onFerme} />
        </div>
      </div>
    </Card>
  );
}
